<template lang="pug">
  .card-container.program-card.column.m-bottom-2(v-if='!!program' :class='{"no-margin": noMargin}')
    .card-image-info.column.space-between
      img.novelty-badge(:src='require(`../assets/${language}_noveltyBadge.svg`)' width='110px' height='110px' v-if='program.novelty')
      .card-image(:style='getProgramBackgroundImage(program.thumbnail || "")' :class='{ blur: isComingSoon }')
      .color-tag.krea.m-top-2.m-left-2.position-1(v-if='isKrea')
      .color-tag.m-top-2.m-left-2.position-1(:style='setTagColor' v-else)
        .card-button.m-top-1.row.middle(v-if='isComingSoon')
          simple-svg.white-stroke-icon.m-right-1(:filepath='calendarIcon' :width='"20px"' :height='"20px"')
          span.text-normal.bold.white {{ $t('programCard.comingSoon') }}
      router-link.card-button.border-button.m-left-2.m-bottom-2.row.middle.position-1(v-if='showAskInfo' :to='programLink')
        simple-svg.image-container.white-stroke-icon.m-right-1.m-left-1(:filepath='infoIcon' :width='"20px"' :height='"20px"')
        span.text-normal.bold.white {{ $t('programCard.askInfo') }}
      router-link.card-button.border-button.m-left-2.m-bottom-2.row.middle.position-1(v-if='isComingSoon' :to='programLink')
        simple-svg.image-container.white-stroke-icon.m-right-1.m-left-1(:filepath='infoIcon' :width='"20px"' :height='"20px"')
        span.text-normal.bold.white {{ $t('programCard.knowMore') }}
    .card-info.item-1.relative
      options.m-top-1.absolute(
        v-if='showOptions'
        v-on-clickaway='toggleOptions'
        :program='program'
        :approvedLanguages='approvedLanguages'
        :availableLanguages='availableLanguages'
        :disapprovedLanguages='disapprovedLanguages'
        :isTrial='isTrial'
        :isOwner='false'
        :draft='draft'
      )
      .row.middle.m-bottom-05.self-end.flags
        simple-svg(
          v-for='language in draft ? disapprovedLanguages : approvedLanguages'
          :filepath='getLanguageIcon(language)'
          width='21px'
          height='21px'
        )
        button.m-left-05(v-if='withOptions' @click='toggleOptions')
          simple-svg(:filepath='optionsIcon' width='15px' height='15px')
      router-link.text-big.blue.bold(:to='programLink') {{ program.name || $t("programCard.untitled") }}
      .column.m-bottom-1.wrap.middle.m-top-auto
        .used-content.row.middle.m-bottom-1(v-if='program.usedEducationalProgram' @click='showUsedContentDrawer')
          simple-svg.fern-green-icon.m-right-05(:filepath='checkIcon' width='15px' height='15px')
          span.text-small.fern-green.underline.pointer.m-left-05 {{ $t("programCard.seeUsedRecord") }}
        .row(v-if='isTeachersTraining' :style='teachersTrainingBadge')
          span.teachers-training-badge.text-small.relative {{ $t('resourceCard.category.teachersTraining') }}
        .row.middle
          p.row.middle.text-normal.age-desc.m-right-3(
            v-if='program.ageRange && !isTeachersTraining'
            :style='getAgeRangeStyle(program.ageRange.mainColor)'
          ) {{ $t('programCard.ageBetween', { startAge: program.ageRange.minAge, endAge: program.ageRange.maxAge }) }}
          p.row.middle.text-normal.age-desc(
            v-if='program.secondaryAgeRange && !isTeachersTraining'
            :style='getAgeRangeStyle(program.secondaryAgeRange.mainColor)'
          ) {{ $t('programCard.ageBetween', { startAge: program.secondaryAgeRange.minAge, endAge: program.secondaryAgeRange.maxAge }) }}
      .row.middle.m-top-1
        img.authors(:src='programAuthor.picture || defaultProfileImage')
        span.text-small.m-left-1.dark-gray {{ programAuthor.name || $t("programCard.untitled") }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { educationalProgramStatus, educationalProgramLanguageStatus } from '../utils/constants'
import infoIcon from '@/assets/ic_info.svg'
import calendarIcon from '@/assets/ic_calendar.svg'
import userRole from '../mixins/userRole'
import { mixin as clickaway } from 'vue-clickaway'
import { allLanguages } from '../constants/languages'
import defaultProfileImage from '@/assets/empty_profile.svg'
import optionsIcon from '@/assets/ic_dots_menu.svg'
import { routes } from '../router'
import checkIcon from '@/assets/ic_check.svg'
import { teachersTrainingReferenceId } from '../constants/resource'
import Options from './EducationalPrograms/ProgramCardOptions'

export default {
  mixins: [userRole, clickaway],
  components: { Options },
  data() {
    return {
      routes,
      checkIcon,
      infoIcon,
      calendarIcon,
      optionsIcon,
      defaultProfileImage,
      showOptions: false
    }
  },
  computed: {
    ...mapState({
      language: state => state.behavior.locale,
      categories: state => state.dashboard.categories
    }),
    teachersTrainingBadge() {
      return {
        '--category-color': this.program.categoryColor
      }
    },
    setTagColor() {
      return {
        '--tag-color': this.program.categoryColor
      }
    },
    isComingSoon() {
      return this.program.activatedLanguages[this.program.searchLocale] === educationalProgramLanguageStatus.comingSoon
    },
    isApproved() {
      return this.program.activatedLanguages[this.program.searchLocale] === educationalProgramLanguageStatus.approved
    },
    isPurchased() {
      return this.program.purchasedStatus === educationalProgramStatus.purchased
    },
    isNotPurchased() {
      return this.program.purchasedStatus === educationalProgramStatus.notPurchased
    },
    showAskInfo() {
      return this.isNotPurchased && this.isApproved
    },
    isTeachersTraining() {
      return this.program.categoryReferenceId === teachersTrainingReferenceId
    },
    isKrea() {
      return this.program.categoryId === Number(process.env.VUE_APP_KREA_CATEGORY_ID)
    },
    isPublished() {
      return this.isKrea && this.approvedLanguages.length
    },
    programAuthor() {
      return this.program.author || this.program.mainAuthor
    },
    availableLanguages() {
      return Object.keys(this.program.activatedLanguages)
    },
    approvedLanguages() {
      return this.availableLanguages.filter(key => this.program.activatedLanguages[key] !== educationalProgramLanguageStatus.hidden)
    },
    disapprovedLanguages() {
      return this.availableLanguages.filter(key => this.program.activatedLanguages[key] === educationalProgramLanguageStatus.hidden)
    },
    programLink() {
      if (this.goToEdition) {
        return {
          name: routes.kreaEducationalContent,
          params: { id: this.program.id },
          query: {
            locale: this.draft
              ? (this.availableLanguages.includes(this.language) && this.language) || this.availableLanguages[0]
              : (this.approvedLanguages.includes(this.language) && this.language) || this.approvedLanguages[0]
          }
        }
      } else {
        return {
          name: routes.educationalProgram,
          params: { id: this.program.id }
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'showElement',
      'setKreaEducationalContentStatus'
    ]),
    showUsedContentDrawer() {
      this.showElement({
        element: 'usedContentDrawer',
        props: { contentId: this.program.id }
      })
    },
    getProgramBackgroundImage(url) {
      return {
        '--background-image': `url("${/[\s()]+/.test(url) ? encodeURI(url) : url}")`
      }
    },
    getAgeRangeStyle(color) {
      return {
        '--age-color': color
      }
    },
    getLanguageIcon(language) {
      return allLanguages.find(lang => lang.lang === language).icon
    },
    toggleOptions() {
      this.showOptions = !this.showOptions
    },
    showPublishContentModal() {
      this.showElement({
        element: 'kreaPublishContentModal',
        props: {
          contentId: this.program.id,
          keywords: this.program.keywords,
          languages: this.disapprovedLanguages,
          completeness: this.program.completeness
        }
      })
    },
    showEditKeywordsModal() {
      this.showElement({
        element: 'kreaEditKeywordsContentModal',
        props: {
          contentId: this.program.id,
          keywords: this.program.keywords,
          availableLanguages: this.program.availableLanguages
        }
      })
    },
    showDeleteContentModal() {
      this.showElement({
        element: 'kreaDeleteModal',
        props: {
          id: this.program.id,
          languages: this.availableLanguages,
          type: 'content'
        }
      })
    },
    showUnpublishContentModal() {
      this.showElement({
        element: 'kreaUnpublishContentModal',
        props: {
          contentId: this.program.id,
          contentTitle: this.program.title,
          languages: this.approvedLanguages
        }
      })
    }
  },
  props: {
    program: { type: Object, required: true },
    withOptions: { type: Boolean },
    draft: { type: Boolean },
    goToEdition: { type: Boolean },
    noMargin: { type: Boolean }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.hidden{
  display: none;
}

.teachers-training-badge {
  display: inline-flex;
  width: auto;
  padding-left: 16px;

  &::before {
    background-color: var(--category-color);
    border-radius: 100%;
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    top: 6px;
    width: 8px;
  }
}

.program-card {
  height: 370px;
  position: relative;
  &.no-margin{
    margin: 0;
  }
}

.flags{
  &>:not(:last-child){
    margin-right: 5px;
  }
}

.badge {
  position: absolute;
  right: -11px;
  top: -10px;
  z-index: 1;
}

.novelty-badge {
  position: absolute;
  right: -1px;
  top: 0px;
  transform: rotate(-1deg);
  z-index: 1;
  filter: drop-shadow(2px 2px 5px $shadow-blacker);
}

.border-button {
  border: 2px solid $white;
  border-radius: 4px;
}

.card-button {
  color: $white;
  height: 40px;
  width: 210px;
  background: transparent;
}

.card-image {
  background-image: var(--background-image), linear-gradient(180deg, $black 0%, $dark-gray-translusent 100%);
  background-size: cover;
  height: 100%;
  width: 100%;
  position: absolute;
}

.card-image-info {
  border-radius: 10px 10px 0 0;
  height: 160px;
  width: 100%;
  position: relative;
  background-color: transparent;
  overflow: hidden;
}

.card-info{
  padding: 14px;
  margin: 0;
}

.blur {
  filter: blur(10px);
  overflow: hidden;
}

.position-1 {
  z-index: 1;
}

.image-container {
  max-height: 20px;
  max-width: 20px;
}
</style>
